import * as Cesium from "cesium";

export const PROPERTY_STANDARDS = {
  left_line_type: ["virtual", "marker", "turning"],
  right_line_type: ["virtual", "marker", "turning"],
  speed_limit: ["5mph", "10mph", "15mph", "20mph", "25mph", "30mph", "35mph", "40mph", "45mph", "50mph", "55mph", "60mph", "65mph", "70mph", "75mph", "80mph", "85mph", "90mph"],
  one_way: ["yes", "no"],
  turn_direction: ["straight", "left", "right", "exit_right", "exit_left", "merge_right", "merge_left", "split_right", "split_left", "u_turn_left", "u_turn_right"],
  subtype: ["road", "shoulder"],
};

function normalizeValue(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim().toLowerCase();
}

function isValueAllowed(value, allowedList) {
  if (!Array.isArray(allowedList) || allowedList.length === 0) return true;

  const allowedNorm = allowedList.map((x) => normalizeValue(x));

  if (Array.isArray(value)) {
    return value.every((item) => allowedNorm.includes(normalizeValue(item)));
  }

  const norm = normalizeValue(value);
  return allowedNorm.includes(norm);
}

function getPropertyValueFromProps(props, key) {
  if (!props) return undefined;

  if (typeof props.get === "function") {
    const property = props.get(key);
    if (!property) return undefined;
    if (typeof property.getValue === "function") return property.getValue();
    return property;
  }

  if (props._properties && typeof props._properties === "object") {
    const property = props._properties[key];
    if (!property) return undefined;
    if (typeof property.getValue === "function") return property.getValue();
    return property;
  }

  if (typeof props.getValue === "function") {
    const obj = props.getValue();
    if (obj && typeof obj === "object") return obj[key];
  }

  if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];

  return undefined;
}

function getEntityId(entity) {
  if (!entity) return undefined;
  const props = entity.properties;
  let idFromProps;

  if (props) {
    if (typeof props.get === "function") {
      const property = props.get("id");
      if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
    }

    if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
      const property = props._properties.id;
      if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
    }

    if (idFromProps === undefined && typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
        idFromProps = obj.id;
      }
    }

    if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
      idFromProps = props.id;
    }
  }

  if (idFromProps !== undefined && idFromProps !== null) return idFromProps;
  return entity.id;
}

function formatAllowedList(allowedList, maxItems = 12) {
  if (!Array.isArray(allowedList)) return "";
  if (allowedList.length <= maxItems) return allowedList.join(", ");
  return `${allowedList.slice(0, maxItems).join(", ")}, ...`;
}

// Main function: returns [{ entity, id, reasons }] for entities that violate PROPERTY_STANDARDS
export function getEntitiesWithInvalidPropertyEnums(dataSource) {
  if (!dataSource || !dataSource.entities) return [];

  const entities = dataSource.entities.values || dataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  const standardKeys = Object.keys(PROPERTY_STANDARDS);
  if (standardKeys.length === 0) return [];

  const result = [];
  const seenIds = new Set();

  for (let i = 0; i < entities.length; i++) {
    const entity = entities[i];
    if (!entity || !entity.properties) continue;

    const props = entity.properties;
    const reasons = [];

    for (let k = 0; k < standardKeys.length; k++) {
      const key = standardKeys[k];
      const allowedList = PROPERTY_STANDARDS[key];

      const value = getPropertyValueFromProps(props, key);
      if (value === undefined) continue;

      if (!isValueAllowed(value, allowedList)) {
        const allowedStr = formatAllowedList(allowedList);
        const valueStr = Array.isArray(value) ? value.map((v) => String(v)).join(", ") : String(value);

        reasons.push(`Invalid value for key: ${key} (value: ${valueStr}${allowedStr ? `, allowed: ${allowedStr}` : ""})`);
      }
    }

    if (reasons.length > 0) {
      const id = getEntityId(entity);
      const dedupeKey = id ?? entity;

      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({ entity, id, reasons });
      }
    }
  }

  return result;
}
